<template>
  <div>
    <custom-style v-if="primaryColorCss" :css="primaryColorCss" />
    <custom-style :css="customCss" />
  </div>
</template>

<script>
import {
  wencesApi,
  getWencesApiBaseUrl,
  getCurrentClientDbName,
} from "@/api/wencesApi";

/** <link>/<meta> del <head>, creándolo si falta. */
function headTag(selector, create) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el;
}

function hexToRgb(hex) {
  const cleaned = String(hex).replace(/^#/, "");
  if (!/^[0-9a-fA-F]{6}$/.test(cleaned)) return null;
  const r = parseInt(cleaned.slice(0, 2), 16);
  const g = parseInt(cleaned.slice(2, 4), 16);
  const b = parseInt(cleaned.slice(4, 6), 16);
  return `${r}, ${g}, ${b}`;
}

function buildPrimaryColorCss(hex) {
  if (!hex || !hex.trim()) return "";
  const color = hex.trim();
  const rgb = hexToRgb(color);
  if (!rgb) return "";
  return `
:root {
  --md-theme-default-primary: ${color} !important;
  --bs-primary: ${color} !important;
  --bs-primary-rgb: ${rgb} !important;
  --bs-primary-text-emphasis: ${color} !important;
  --bs-primary-bg-subtle: ${color} !important;
  --bs-primary-border-subtle: ${color} !important;
}
`;
}

const CustomStyle = {
  functional: true,
  props: {
    css: {
      type: String,
      required: true,
    },
  },
  render(h, { props }) {
    return h("style", {
      attrs: {
        type: "text/css",
      },
      domProps: {
        textContent: props.css,
      },
    });
  },
};

export default {
  name: "CustomCss",
  components: {
    CustomStyle,
  },
  data() {
    return {
      customCss: "",
      primaryColor: "",
      favicon: "",
    };
  },
  computed: {
    primaryColorCss() {
      return buildPrimaryColorCss(this.primaryColor);
    },
  },
  async created() {
    await this.loadSettings();
  },
  mounted() {
    this.$root.$on("settings-saved", this.loadSettings);
  },
  beforeDestroy() {
    this.$root.$off("settings-saved", this.loadSettings);
  },
  methods: {
    async loadSettings() {
      try {
        const response = await wencesApi.get("/api/settings");
        if (response.data.success && response.data.data) {
          const data = response.data.data;
          this.customCss = data.customCss || "";
          this.primaryColor = data.primaryColor || "";
          this.favicon = data.favicon || "";
          this.applyFavicon(this.favicon);
          this.applyAppBranding(data);
        }
      } catch (error) {
        console.error("Error loading settings for CSS/theme:", error);
      }
    },
    /**
     * App instalada con la marca del tenant: manifest e íconos los arma el back con el
     * nombre, logo y color de Ajustes. Sin marca propia queda el manifest de ExportAI.
     */
    applyAppBranding(data) {
      const db = getCurrentClientDbName();
      if (!db || !(data.siteName || data.favicon || data.logo)) return;
      const base = `${getWencesApiBaseUrl()}/api/public/app/${encodeURIComponent(db)}`;
      const manifest = headTag('link[rel="manifest"]', () => {
        const l = document.createElement("link");
        l.rel = "manifest";
        return l;
      });
      manifest.href = `${base}/manifest.webmanifest?origin=${encodeURIComponent(window.location.origin)}`;
      // iPhone no usa el manifest para el ícono ni el nombre de la pantalla de inicio.
      if (data.favicon || data.logo) {
        headTag('link[rel="apple-touch-icon"]', () => {
          const l = document.createElement("link");
          l.rel = "apple-touch-icon";
          return l;
        }).href = `${base}/icon-180.png`;
      }
      if (data.siteName) {
        headTag('meta[name="apple-mobile-web-app-title"]', () => {
          const m = document.createElement("meta");
          m.name = "apple-mobile-web-app-title";
          return m;
        }).content = data.siteName;
      }
      headTag('meta[name="apple-mobile-web-app-capable"]', () => {
        const m = document.createElement("meta");
        m.name = "apple-mobile-web-app-capable";
        return m;
      }).content = "yes";
    },
    applyFavicon(faviconData) {
      let link = document.querySelector('link[rel="icon"]');
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = faviconData && faviconData.trim() ? faviconData.trim() : (process.env.BASE_URL || "/") + "img/gotakeitfavicon.png";
    },
  },
};
</script>

